import { ReactNode, useCallback, useEffect, useState, memo, useMemo } from "react";
import { FocusedInput, OnDatesChangeProps, START_DATE } from "@datepicker-react/hooks";
import { addDays, addMonths, differenceInDays, format, isLastDayOfMonth, parseISO, toDate } from "date-fns";
import { useSearchParams } from "next/navigation";
import DateRangeInputGeneral from "components/date-picker/date-range-input/date-range-input-general";
import useCalendarCoverage from "hooks/swr/useCalendarCoverage";
import { selectUser } from "stores/auth/authSlice";
import { markBookingStore, selectBookingStore } from "stores/booking/bookingSlice";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { markMultiUnitStore } from "stores/multi-unit-detail/multiUnitSlice";
import { markSearch, selectSearch } from "stores/search/searchSlice";
import { CurrentSearchTabType } from "stores/ui/types";
import { markSearchFlow, selectUI } from "stores/ui/uiSlice";
import { isDesktop } from "utils/dom";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
type SearchDateInputProps = {
  DateInputModalFooter?: ReactNode;
  DateInputModalHeader?: ReactNode;
  focusedInput: FocusedInput;
  onFocusChange: (focusInput: FocusedInput) => void;
  popoverStyle?: string;
  beforeCalendar?: ({
    handleDateReset,
    onClose
  }: {
    handleDateReset?: () => void;
    onClose?: () => void;
  }) => ReactNode;
  isDisplayClearDates?: boolean;
};
const SearchDateInputWithoutMemo = ({
  onFocusChange,
  focusedInput,
  DateInputModalHeader,
  DateInputModalFooter,
  popoverStyle = "!top-[90px] left-0 md:left-[-20px] w-[925px] max-w-[90vw] z-[1] border border-[#e2e5e5] px-3 py-2 !rounded-[20px] bg-offWhite shadow-[0px_3px_6px_#61797c29]",
  beforeCalendar,
  isDisplayClearDates = false
}: SearchDateInputProps) => {
  const [isLargerThan992] = useMediaQuery("(min-width: 992px)");
  const searchParams = useSearchParams();
  const bookingStore = useAppSelector(selectBookingStore);
  const search = useAppSelector(selectSearch);
  const {
    currentSearchTab
  } = useAppSelector(selectUI);
  const dispatch = useAppDispatch();
  const handleSearchFlow = useCallback((newCurrentSearchTab: CurrentSearchTabType) => {
    dispatch(markSearchFlow({
      currentSearchTab: newCurrentSearchTab
    }));
  }, [dispatch]);
  const {
    data
  } = useCalendarCoverage();
  const coverage = data?.coverage ?? 180;
  const maxBookingDate = useMemo(() => {
    return addDays(new Date(), focusedInput === "startDate" ? coverage - 1 : coverage);
  }, [coverage, focusedInput]);
  const [isDateSelected, setIsDateSelected] = useState(false);
  const onCloseCalendar = useCallback(() => {
    handleSearchFlow(null); //  This is for closing the calendar when the user is on the search page
    onFocusChange(START_DATE);
    dispatch(markBookingStore({
      isCalendarOpen: false
    })); // Closes the calendar on the detail page
    dispatch(markMultiUnitStore({
      isMultiUnitsCalendarOpen: false
    })); // Closes the calendar on the multi-unit detail page
  }, [dispatch, handleSearchFlow, onFocusChange]);
  const onDatesChange = useCallback((newDates: OnDatesChangeProps) => {
    if (!newDates.focusedInput && newDates.startDate === newDates.endDate) {
      return;
    }
    const startDate = newDates.startDate ? format(newDates.startDate, "yyyy-MM-dd") : null;
    const endDate = newDates.endDate ? format(newDates.endDate, "yyyy-MM-dd") : null;
    // can't select same date for booking
    if (startDate === endDate) {
      return;
    }
    const commonPayload = {
      startDate,
      endDate
    };
    dispatch(markSearch(commonPayload));
    onFocusChange(newDates.focusedInput);
    if (startDate && endDate) {
      let noOfDays = 0;
      noOfDays = differenceInDays(parseISO(endDate), parseISO(startDate));
      // This is for updating the no of days on the search page
      dispatch(markSearch({
        noOfDays: noOfDays || 0
      }));
      // This is for updating the no of days on the multi-unit detail page
      dispatch(markMultiUnitStore({
        noOfDays: noOfDays || 0,
        page: 0
      }));
    }
  }, [dispatch, onFocusChange]);

  // This Effect is for opening the calendar when the user is on the details page
  useEffect(() => {
    if (bookingStore.isCalendarOpen && focusedInput === null) {
      onFocusChange(START_DATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingStore.isCalendarOpen]);

  // This Effect is for opening the calendar when the user is on the search page
  useEffect(() => {
    if (currentSearchTab === "dates" && focusedInput === null) {
      onFocusChange(START_DATE);
    }
  }, [focusedInput, onFocusChange, currentSearchTab]);
  const onOpenCalendar = useCallback(() => {
    onFocusChange(START_DATE);
    handleSearchFlow("dates"); // This is for opening the calendar when the user is on the search page
  }, [handleSearchFlow, onFocusChange, currentSearchTab, dispatch]);
  const onReset = useCallback(() => {
    const resetDateSelection = {
      startDate: null,
      endDate: null,
      noOfDays: 0
    };
    // Reset dates when 'Clear Dates' is clicked on the search page
    dispatch(markSearch(resetDateSelection));
    onFocusChange(START_DATE);
    setIsDateSelected(false);
    if (isDesktop()) {
      onCloseCalendar();
    }
  }, [dispatch, onCloseCalendar, onFocusChange]);
  const user = useAppSelector(selectUser);
  const isAdmin = user?.isAdmin;
  const allowSameDay = searchParams?.get("allowSameDay") === "true" || isAdmin;
  return <DateRangeInputGeneral isOpen={currentSearchTab === "dates"} onClose={onCloseCalendar} onOpen={onOpenCalendar} minBookingDate={allowSameDay ? new Date() : addDays(new Date(), 1)} maxBookingDate={maxBookingDate} onDatesChange={onDatesChange} startDate={search?.startDate && toDate(parseISO(search?.startDate)) || null} endDate={search?.endDate && toDate(parseISO(search?.endDate)) || null} focusedInput={focusedInput} onFocusChange={onFocusChange} onReset={onReset} isDateSelected={isDateSelected} initialVisibleMonth={isLastDayOfMonth(new Date()) ? addMonths(new Date(), 1) : new Date()}
  // buttonStyle={buttonStyle}
  vertical={!isLargerThan992}
  // for mobile view
  modalHeader={DateInputModalHeader} modalFooter={DateInputModalFooter}
  // for vacation-rental page & regional-landing page
  popoverStyle={popoverStyle} beforeCalendar={beforeCalendar} isDisplayClearDates={isDisplayClearDates} data-sentry-element="DateRangeInputGeneral" data-sentry-component="SearchDateInputWithoutMemo" data-sentry-source-file="SearchDateInput.tsx" />;
};
const SearchDateInput = memo(SearchDateInputWithoutMemo);
export default SearchDateInput;